// The review surface for one custom MCP server's tools: which of them nobody has vouched for
// yet, and the classification that lets one out of quarantine. Pure: the page owns the requests.
import type { NativeToolEffect } from '@parcel/tools';
import { Badge, Button } from '@parcel/ui';
import { Ban, RefreshCw } from 'lucide-react';
import type {
  ConnectionToolPolicyRow,
  ConnectionToolRefreshCounts,
  MissingConnectionTool,
} from '../../lib/spec-connection-tools.js';

/** The four classes an owner or admin can put a custom tool in, strictest last. */
const EFFECT_CHOICES: readonly { effect: NativeToolEffect; label: string }[] = [
  { effect: 'read', label: 'Read' },
  { effect: 'reversible_write', label: 'Reversible write' },
  { effect: 'destructive', label: 'Destructive' },
  { effect: 'admin_security', label: 'Admin security' },
];

const EFFECT_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  EFFECT_CHOICES.map(({ effect, label }) => [effect, label]),
);

const NEVER_REVIEWED =
  'No owner or admin has reviewed what this tool does yet, so it is blocked until one does.';

const REVIEW_WENT_STALE =
  "This tool's inputs changed after it was reviewed, so Parcel blocked it again. Check what it takes now, then classify it.";

const NO_TOOLS = 'This server advertises no tools right now.';

const MISSING_TOOLS =
  'The server stopped offering these, so Spec cannot use them. Their reviews are kept: if the server offers them again, they come back classified as they are here.';

/** Which of the two ways a custom tool ends up blocked applies, in plain language. */
function describeUnreviewedTool(tool: Pick<ConnectionToolPolicyRow, 'reason'>): string {
  return tool.reason === 'drift_blocked' ? REVIEW_WENT_STALE : NEVER_REVIEWED;
}

/** What one discovery run moved, for the line a refresh leaves behind. */
export function describeToolRefresh(counts: ConnectionToolRefreshCounts): string {
  const noun = counts.total === 1 ? 'tool' : 'tools';
  return `${counts.total} ${noun} now: ${counts.added} added, ${counts.updated} changed, ${counts.removed} removed.`;
}

interface ClassifyControlProps {
  tool: ConnectionToolPolicyRow;
  saving: boolean;
  onClassify: (tool: ConnectionToolPolicyRow, effect: NativeToolEffect) => void;
}

/** Native same-name radios, as in spec-tool-policy-control.tsx: the browser already gives a
    group correct roving-focus arrow-key behavior, so there is nothing to hand-roll. */
function ClassifyControl({ tool, saving, onClassify }: ClassifyControlProps) {
  const groupName = `classify-${tool.toolKey}`;
  return (
    <fieldset className="flex flex-wrap items-center gap-3">
      <legend className="sr-only">{`Classify ${tool.toolKey}`}</legend>
      {EFFECT_CHOICES.map(({ effect, label }) => (
        <label
          key={effect}
          className="flex items-center gap-1.5 text-xs"
          htmlFor={`${groupName}-${effect}`}
        >
          {/* Never checked: a row leaves this list the moment its classification lands. */}
          <input
            type="radio"
            id={`${groupName}-${effect}`}
            name={groupName}
            value={effect}
            checked={false}
            disabled={saving}
            onChange={() => onClassify(tool, effect)}
          />
          {label}
        </label>
      ))}
    </fieldset>
  );
}

export interface ConnectionToolReviewProps {
  tools: readonly ConnectionToolPolicyRow[];
  /** Reviewed tools the last refresh did not see. Empty for a curated connection. */
  missingTools: readonly MissingConnectionTool[];
  /** True for an owner/admin; the API enforces this, and this only mirrors it. */
  canManage: boolean;
  classifyingToolKeys: ReadonlySet<string>;
  classifyErrors: Readonly<Record<string, string | null>>;
  refreshing: boolean;
  refreshSummary: string | null;
  refreshError: string | null;
  onClassify: (tool: ConnectionToolPolicyRow, effect: NativeToolEffect) => void;
  onRefresh: () => void;
}

export function ConnectionToolReview({
  tools,
  missingTools,
  canManage,
  classifyingToolKeys,
  classifyErrors,
  refreshing,
  refreshSummary,
  refreshError,
  onClassify,
  onRefresh,
}: ConnectionToolReviewProps) {
  const unreviewed = tools.filter((tool) => tool.unreviewed);

  return (
    <section className="space-y-3" data-testid="connection-tool-review">
      <div className="flex items-start justify-between gap-4 border-b pb-2">
        <div>
          <h3 className="text-sm font-medium">Tool review</h3>
          <p className="text-xs text-muted-foreground">
            Parcel blocks every tool this server offers until an owner or admin says what it does.
          </p>
        </div>
        {canManage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={refreshing}
            onClick={onRefresh}
          >
            <RefreshCw className="size-3.5" aria-hidden />
            {refreshing ? 'Refreshing…' : 'Refresh tools'}
          </Button>
        ) : null}
      </div>

      {refreshError ? (
        <p role="alert" className="text-sm text-destructive">
          {refreshError}
        </p>
      ) : null}
      {refreshSummary ? <p className="text-xs text-muted-foreground">{refreshSummary}</p> : null}

      {unreviewed.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tools.length === 0 ? NO_TOOLS : 'Every tool this server offers has been reviewed.'}
        </p>
      ) : (
        <div className="divide-y">
          {unreviewed.map((tool) => (
            <div
              key={tool.toolKey}
              data-testid="connection-tool-review-row"
              data-tool-key={tool.toolKey}
              className="space-y-2 py-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {/* The server's own name for it, verbatim: matching what the admin is
                      reviewing against what the server advertises is the whole job here. */}
                  <p className="truncate font-mono text-sm">{tool.toolKey}</p>
                  <p className="text-xs text-muted-foreground">{tool.description}</p>
                </div>
                <Badge variant="red" className="shrink-0 gap-1 font-normal">
                  <Ban className="size-3.5" aria-hidden />
                  Blocked
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{describeUnreviewedTool(tool)}</p>
              {canManage ? (
                <ClassifyControl
                  tool={tool}
                  saving={classifyingToolKeys.has(tool.toolKey)}
                  onClassify={onClassify}
                />
              ) : null}
              {classifyErrors[tool.toolKey] ? (
                <p role="alert" className="text-xs text-destructive">
                  {classifyErrors[tool.toolKey]}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {missingTools.length > 0 ? (
        <div data-testid="connection-tool-missing" className="space-y-2 border-t pt-3">
          <h4 className="text-sm font-medium">No longer offered</h4>
          <p className="text-xs text-muted-foreground">{MISSING_TOOLS}</p>
          <ul className="space-y-1">
            {missingTools.map((tool) => (
              <li
                key={tool.toolKey}
                data-tool-key={tool.toolKey}
                className="flex items-center gap-2"
              >
                <span className="truncate font-mono text-sm">{tool.toolKey}</span>
                {tool.effect ? (
                  <Badge variant="outline" className="shrink-0 font-normal">
                    {EFFECT_LABELS[tool.effect] ?? tool.effect}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
